import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Crown, ArrowRight, ArrowUpDown, TrendingUp, TrendingDown,
  RefreshCw, Shield, Coins, Globe, LogOut, User, Bell, Wallet
} from 'lucide-react';
import { toast } from 'sonner';

const FIAT_CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CNY'];
const CRYPTO_CURRENCIES = ['BTC', 'ETH', 'USDT', 'USDC', 'DAI'];

const KALMarket = () => {
  const { user, token, logout } = useAuth();
  const [rates, setRates] = useState(null);
  const [kalWallet, setKalWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('buy');

  // Buy form
  const [buyAmount, setBuyAmount] = useState('');
  const [buyCurrency, setBuyCurrency] = useState('USD');
  const [buyEstimate, setBuyEstimate] = useState(null);

  // Sell form
  const [sellAmount, setSellAmount] = useState('');
  const [sellCurrency, setSellCurrency] = useState('USD');
  const [sellEstimate, setSellEstimate] = useState(null);

  const [processing, setProcessing] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchData = useCallback(async () => {
    try {
      const ratesRes = await axios.get(`${API}/kal/rates`);
      setRates(ratesRes.data);

      if (user) {
        try {
          const walletRes = await axios.get(`${API}/kal/wallet`, { headers, withCredentials: true });
          setKalWallet(walletRes.data);
        } catch {
          setKalWallet(null);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (rates && buyAmount && buyCurrency) {
      const rateKey = `KAL_${buyCurrency}`;
      const rate = rates.rates?.[rateKey];
      if (rate) setBuyEstimate({ cost: (parseFloat(buyAmount) * rate).toFixed(4), rate });
      else setBuyEstimate(null);
    } else setBuyEstimate(null);
  }, [buyAmount, buyCurrency, rates]);

  useEffect(() => {
    if (rates && sellAmount && sellCurrency) {
      const rateKey = `KAL_${sellCurrency}`;
      const rate = rates.rates?.[rateKey];
      if (rate) setSellEstimate({ proceeds: (parseFloat(sellAmount) * rate).toFixed(4), rate });
      else setSellEstimate(null);
    } else setSellEstimate(null);
  }, [sellAmount, sellCurrency, rates]);

  const handleBuy = async () => {
    if (!buyAmount || parseFloat(buyAmount) <= 0) return toast.error('Enter a valid amount');
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/kal/buy`, {
        amount_kal: parseFloat(buyAmount),
        payment_currency: buyCurrency
      }, { headers, withCredentials: true });
      toast.success(`Bought ${res.data.amount_kal} TNB for ${res.data.cost} ${buyCurrency}`);
      setBuyAmount('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleSell = async () => {
    if (!sellAmount || parseFloat(sellAmount) <= 0) return toast.error('Enter a valid amount');
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/kal/sell`, {
        amount_kal: parseFloat(sellAmount),
        receive_currency: sellCurrency
      }, { headers, withCredentials: true });
      toast.success(`Sold ${res.data.amount_kal} TNB for ${res.data.proceeds} ${sellCurrency}`);
      setSellAmount('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sale failed');
    } finally {
      setProcessing(false);
    }
  };

  const createKalWallet = async () => {
    try {
      await axios.post(`${API}/kal/wallet`, {}, { headers, withCredentials: true });
      toast.success('TNB wallet created with 1,000 TNB starting balance!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create wallet');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-12 w-12 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="tnb-market-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <div className="w-9 h-9 gold-gradient rounded-sm flex items-center justify-center">
                <Crown className="h-5 w-5 text-black" />
              </div>
              <span className="font-serif text-lg font-semibold hidden sm:block">TANUB</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">Home</Link>
              <Link to="/tnb/transparency" className="text-sm text-muted-foreground hover:text-foreground">Transparency</Link>
              <Link to="/public" className="text-sm text-muted-foreground hover:text-foreground">CBDC Portal</Link>
              <Link to="/bonds" className="text-sm text-muted-foreground hover:text-foreground">Bonds</Link>
            </div>
            <div className="flex items-center gap-3">
              {user ? (
                <>
                  <Link to="/citizen"><Button variant="ghost" size="sm">Dashboard</Button></Link>
                  <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn"><LogOut className="h-4 w-4" /></Button>
                </>
              ) : (
                <Link to="/login"><Button className="btn-royal-gold" size="sm" data-testid="login-btn">Sign In</Button></Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Price Banner */}
        <div className="mb-8 p-6 rounded-xl bg-black text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Coins className="h-6 w-6 text-gold" />
                <span className="font-mono text-xs uppercase tracking-widest text-gold">TANUB Live Market</span>
              </div>
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-4xl md:text-5xl font-bold text-white" data-testid="tnb-price">
                  ${rates?.kal_usd?.toFixed(2)}
                </span>
                <span className="text-white/60 text-lg">per KAL</span>
              </div>
              <p className="text-white/40 text-sm mt-1">Gold-pegged at 0.001 oz/KAL | Gold: ${rates?.gold_price_usd?.toFixed(2)}/oz</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <div className="text-center px-4 py-2 rounded-lg bg-white/5">
                <p className="text-xs text-white/50 uppercase">Peg Status</p>
                <p className="font-mono text-lg font-bold text-emerald-400" data-testid="peg-status">{rates?.peg_status || 'Stable'}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={fetchData} className="text-white/60 hover:text-white">
                <RefreshCw className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Exchange Rates Panel */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Globe className="h-5 w-5 text-gold" /> Exchange Rates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Fiat Currencies</p>
                  {FIAT_CURRENCIES.map(c => {
                    const rate = rates?.rates?.[`KAL_${c}`];
                    return (
                      <div key={c} className="flex justify-between items-center py-2 border-b border-border/50 last:border-0" data-testid={`rate-${c}`}>
                        <span className="font-medium">{c}</span>
                        <span className="font-mono text-sm">{rate?.toFixed(4) || '...'}</span>
                      </div>
                    );
                  })}
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3 mt-4">Crypto</p>
                  {CRYPTO_CURRENCIES.map(c => {
                    const rate = rates?.rates?.[`KAL_${c}`];
                    const marketData = rates?.crypto_market?.[c];
                    const change = marketData?.change_24h;
                    return (
                      <div key={c} className="flex justify-between items-center py-2 border-b border-border/50 last:border-0" data-testid={`rate-${c}`}>
                        <div>
                          <span className="font-medium">{c}</span>
                          {change != null && (
                            <span className={`ml-2 text-xs ${change >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                              {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                            </span>
                          )}
                        </div>
                        <span className="font-mono text-sm">{rate != null ? rate.toFixed(8) : '...'}</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-center py-2 mt-2 bg-gold/5 rounded px-2">
                    <span className="font-semibold text-gold-dark">XAU (Gold oz)</span>
                    <span className="font-mono text-sm font-bold">0.001000</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* TNB Wallet */}
            {user && (
              <Card className="sovereign-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Wallet className="h-5 w-5" /> TNB Wallet
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {kalWallet ? (
                    <div>
                      <p className="font-mono text-3xl font-bold" data-testid="tnb-balance">
                        {kalWallet.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        <span className="text-sm text-muted-foreground ml-2">KAL</span>
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        ~${(kalWallet.balance * (rates?.kal_usd || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD
                      </p>
                      <p className="font-mono text-xs mt-3 text-muted-foreground truncate">{kalWallet.wallet_id}</p>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-muted-foreground mb-3">No TNB wallet yet</p>
                      <Button onClick={createKalWallet} className="btn-royal-gold" data-testid="create-tnb-wallet-btn">
                        Create TNB Wallet
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Trade Panel */}
          <div className="lg:col-span-2">
            {!user ? (
              <Card className="sovereign-card">
                <CardContent className="p-12 text-center">
                  <Coins className="h-16 w-16 mx-auto mb-4 text-gold" />
                  <h2 className="font-serif text-2xl mb-2">Trade TANUB</h2>
                  <p className="text-muted-foreground mb-6">Sign in to buy, sell, and convert TNB instantly</p>
                  <Link to="/login">
                    <Button className="btn-royal-gold" size="lg" data-testid="signin-to-trade">
                      Sign In to Trade <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : !kalWallet ? (
              <Card className="sovereign-card">
                <CardContent className="p-12 text-center">
                  <Wallet className="h-16 w-16 mx-auto mb-4 text-gold" />
                  <h2 className="font-serif text-2xl mb-2">Create Your TNB Wallet</h2>
                  <p className="text-muted-foreground mb-6">Get started with 1,000 TNB demo balance</p>
                  <Button onClick={createKalWallet} className="btn-royal-gold" size="lg" data-testid="create-wallet-cta">
                    Create TNB Wallet
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="sovereign-card">
                <CardContent className="p-6">
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-2 mb-6">
                      <TabsTrigger value="buy" data-testid="tab-buy">
                        <TrendingUp className="h-4 w-4 mr-2" /> Buy TNB
                      </TabsTrigger>
                      <TabsTrigger value="sell" data-testid="tab-sell">
                        <TrendingDown className="h-4 w-4 mr-2" /> Sell TNB
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="buy">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Amount (TNB)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Enter amount to buy"
                            value={buyAmount}
                            onChange={e => setBuyAmount(e.target.value)}
                            data-testid="buy-amount-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Pay With</Label>
                          <Select value={buyCurrency} onValueChange={setBuyCurrency}>
                            <SelectTrigger data-testid="buy-currency-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[...FIAT_CURRENCIES, ...CRYPTO_CURRENCIES].map(c => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {buyEstimate && (
                          <div className="p-4 rounded-lg bg-muted/50" data-testid="buy-estimate">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Cost</span>
                              <span className="font-mono font-bold">{buyEstimate.cost} {buyCurrency}</span>
                            </div>
                            <div className="flex justify-between text-sm mt-1">
                              <span className="text-muted-foreground">Rate</span>
                              <span className="font-mono">1 TNB = {buyEstimate.rate} {buyCurrency}</span>
                            </div>
                          </div>
                        )}
                        <Button onClick={handleBuy} disabled={processing || !buyAmount} className="w-full btn-royal-gold" size="lg" data-testid="buy-submit-btn">
                          {processing ? 'Processing...' : `Buy TNB`}
                        </Button>
                      </div>
                    </TabsContent>

                    <TabsContent value="sell">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Amount (TNB)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Enter amount to sell"
                            value={sellAmount}
                            onChange={e => setSellAmount(e.target.value)}
                            data-testid="sell-amount-input"
                          />
                          <p className="text-xs text-muted-foreground">Available: {kalWallet?.balance?.toLocaleString()} KAL</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Receive In</Label>
                          <Select value={sellCurrency} onValueChange={setSellCurrency}>
                            <SelectTrigger data-testid="sell-currency-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[...FIAT_CURRENCIES, ...CRYPTO_CURRENCIES].map(c => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {sellEstimate && (
                          <div className="p-4 rounded-lg bg-muted/50" data-testid="sell-estimate">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">You Receive</span>
                              <span className="font-mono font-bold">{sellEstimate.proceeds} {sellCurrency}</span>
                            </div>
                            <div className="flex justify-between text-sm mt-1">
                              <span className="text-muted-foreground">Rate</span>
                              <span className="font-mono">1 TNB = {sellEstimate.rate} {sellCurrency}</span>
                            </div>
                          </div>
                        )}
                        <Button onClick={handleSell} disabled={processing || !sellAmount} className="w-full bg-black hover:bg-black/90 text-white" size="lg" data-testid="sell-submit-btn">
                          {processing ? 'Processing...' : 'Sell TNB'}
                        </Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            )}

            {/* Asset Backing Info */}
            <Card className="sovereign-card mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="h-5 w-5 text-gold" /> Asset Backing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {[
                    { label: 'Gold Reserves', pct: 25, color: 'bg-yellow-500' },
                    { label: 'Natural Resources', pct: 30, color: 'bg-emerald-500' },
                    { label: 'Real Estate', pct: 20, color: 'bg-blue-500' },
                    { label: 'Infrastructure', pct: 15, color: 'bg-purple-500' },
                    { label: 'Govt Revenue', pct: 10, color: 'bg-orange-500' },
                  ].map(item => (
                    <div key={item.label} className="text-center">
                      <div className="relative w-16 h-16 mx-auto mb-2">
                        <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                          <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                          <circle cx="18" cy="18" r="15.5" fill="none" stroke="currentColor"
                            strokeWidth="3" strokeDasharray={`${item.pct} ${100 - item.pct}`}
                            className={item.color.replace('bg-', 'text-')} />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center font-mono text-xs font-bold">{item.pct}%</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{item.label}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <p className="text-sm text-emerald-700 font-medium">115% Reserve Backing Ratio - Fully Collateralized</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default KALMarket;
